import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, FileText, ClipboardList, Users, Package, Scissors, MessageCircle, UserCog, Building2, LogOut, X, MoreHorizontal, Plus, Bot } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import InstallPWAButton from "@/components/InstallPWAButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { QuoteEditorProvider } from "@/context/QuoteEditorContext";

const NAV = [
  { to: "/panel", label: "Asistente", icon: Bot, end: true },
  { to: "/panel/datos", label: "Datos en vivo", icon: LayoutDashboard },
  { to: "/panel/consultas", label: "Consultas", icon: Inbox },
  { to: "/panel/cotizaciones", label: "Cotizaciones", icon: FileText },
  { to: "/panel/ordenes", label: "Órdenes de trabajo", icon: ClipboardList },
  { to: "/panel/clientes", label: "Clientes", icon: Users },
  { to: "/panel/materiales", label: "Materiales", icon: Package },
  { to: "/panel/optimizador", label: "Optimizador de cortes", icon: Scissors },
  { to: "/panel/whatsapp", label: "WhatsApp", icon: MessageCircle },
  { to: "/panel/equipo", label: "Equipo y roles", icon: UserCog },
  { to: "/panel/empresa", label: "Empresa", icon: Building2 },
];

const MOBILE_NAV = [
  { to: "/panel", label: "Asistente", icon: Bot, end: true, matches: ["/panel"] },
  { to: "/panel/datos", label: "Datos", icon: LayoutDashboard, matches: ["/panel/datos"] },
  { to: "/panel/cotizaciones", label: "Ventas", icon: FileText, matches: ["/panel/consultas", "/panel/cotizaciones", "/panel/clientes"] },
  { to: "/panel/ordenes", label: "Trabajos", icon: ClipboardList, matches: ["/panel/ordenes"] },
  { to: "/panel/optimizador", label: "Cortes", icon: Scissors, matches: ["/panel/optimizador"] },
];

const pageTitle = (pathname) => NAV.find((item) => item.end ? pathname === item.to : pathname.startsWith(item.to))?.label || "GR Soluciones";

export default function PanelLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [directOrder, setDirectOrder] = useState(null);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const user = JSON.parse(localStorage.getItem("gr_user") || "{}");

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const logout = () => {
    localStorage.removeItem("gr_token");
    localStorage.removeItem("gr_user");
    navigate("/login");
  };

  const openDirectOrder = () => {
    setQuickOpen(false);
    setDirectOrder({ client_id: "", client_name: "", title: "", notes: "", scheduled_date: "", assigned_to: "" });
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/users").then((r) => setTeam(r.data)).catch(() => {});
  };

  const saveDirectOrder = async () => {
    if (!directOrder.client_name.trim() || !directOrder.title.trim()) { toast.error("Completá cliente y trabajo"); return; }
    try {
      const { data } = await api.post("/orders", { ...directOrder, client_id: directOrder.client_id || null, assigned_to: directOrder.assigned_to || null, scheduled_date: directOrder.scheduled_date || null });
      setDirectOrder(null);
      toast.success(`Orden ${data.number} creada`);
      navigate("/panel/ordenes");
    } catch (e) { toast.error(apiError(e)); }
  };

  const quickNavigate = (to, state) => { setQuickOpen(false); navigate(to, { state }); };

  return (
    <QuoteEditorProvider>
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <header className="md:hidden fixed top-0 inset-x-0 h-16 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10 z-30 flex items-center justify-between px-4 no-print safe-top">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/logo.webp" alt="GR" className="h-9 w-9 object-contain rounded-xl bg-white p-0.5" />
          <div className="min-w-0">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">GR Soluciones</span>
            <span className="block font-heading font-bold text-base leading-tight truncate">{pageTitle(location.pathname)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <InstallPWAButton compact />
          <NotificationBell />
        </div>
      </header>

      {menuOpen && (
        <button className="md:hidden fixed inset-0 bg-black/70 z-40 no-print" onClick={() => setMenuOpen(false)} aria-label="Cerrar menú" />
      )}

      <aside className={`w-[88vw] max-w-sm md:w-64 fixed md:inset-y-0 left-0 bottom-0 md:top-0 bg-[#111] border-r border-neutral-800 flex flex-col z-50 md:z-40 no-print transform transition-transform duration-200 ease-out ${menuOpen ? "translate-y-0 md:translate-x-0" : "translate-y-full md:translate-y-0 md:translate-x-0"} rounded-t-3xl md:rounded-none max-h-[86dvh] md:max-h-none`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-neutral-800">
          <img src="/logo.webp" alt="GR" className="h-10 w-10 object-contain rounded-sm bg-white p-0.5" />
          <span className="font-heading font-bold uppercase tracking-tight text-sm leading-tight flex-1">GR Soluciones<br />Integrales</span>
          <button onClick={() => setMenuOpen(false)} className="md:hidden p-2 rounded-md hover:bg-neutral-800" aria-label="Cerrar menú" data-testid="mobile-menu-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-[#F2B90D]/10 text-[#F2B90D] border-l-2 border-[#F2B90D]" : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-neutral-800">
          <p className="text-xs text-neutral-500 mb-2 truncate">{user.email}</p>
          <button onClick={logout} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 ml-0 md:ml-64 pt-16 md:pt-0 pb-[calc(5.25rem+env(safe-area-inset-bottom))] md:pb-0">
        <div className="hidden md:flex justify-end items-center gap-2 px-8 pt-4 no-print">
          <InstallPWAButton />
          <NotificationBell />
        </div>
        <div className="px-3 sm:px-5 md:px-8 pb-8 pt-4 md:pt-2 overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      {user.role === "admin" && (
        <button onClick={() => setQuickOpen(true)} className="fixed right-4 md:right-7 bottom-[calc(6.25rem+env(safe-area-inset-bottom))] md:bottom-7 z-30 no-print w-14 h-14 rounded-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black shadow-[0_10px_35px_rgba(242,185,13,0.35)] flex items-center justify-center" aria-label="Crear nuevo" data-testid="quick-create-btn">
          <Plus className="w-7 h-7 stroke-[3]" />
        </button>
      )}

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading uppercase">¿Qué querés agregar?</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <button onClick={openDirectOrder} className="flex items-center gap-3 p-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-xl text-left"><ClipboardList className="w-6 h-6 text-[#F2B90D]" /><span><strong className="block">Orden de trabajo</strong><small className="text-neutral-500">Directa, sin cotización previa</small></span></button>
            <button onClick={() => quickNavigate("/panel/optimizador")} className="flex items-center gap-3 p-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-xl text-left"><Scissors className="w-6 h-6 text-[#F2B90D]" /><span><strong className="block">Orden de corte</strong><small className="text-neutral-500">Abrir el optimizador</small></span></button>
            <button onClick={() => quickNavigate("/panel/cotizaciones", { newQuote: true })} className="flex items-center gap-3 p-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-xl text-left"><FileText className="w-6 h-6 text-[#F2B90D]" /><span><strong className="block">Cotización</strong><small className="text-neutral-500">Crear presupuesto nuevo</small></span></button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!directOrder} onOpenChange={(open) => !open && setDirectOrder(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading uppercase">Nueva orden de trabajo</DialogTitle></DialogHeader>
          {directOrder && <div className="space-y-3">
            <div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Cliente</label><select value={directOrder.client_id} onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); setDirectOrder({ ...directOrder, client_id: c?.id || "", client_name: c?.name || directOrder.client_name }); }} className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm"><option value="">Escribir manualmente</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Nombre del cliente</label><Input value={directOrder.client_name} onChange={(e) => setDirectOrder({ ...directOrder, client_name: e.target.value })} placeholder="Ej: Fabio" className="bg-neutral-900 border-neutral-800" /></div>
            <div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Trabajo a realizar</label><Input value={directOrder.title} onChange={(e) => setDirectOrder({ ...directOrder, title: e.target.value })} placeholder="Ej: Reparar portón" className="bg-neutral-900 border-neutral-800" /></div>
            <div className="grid grid-cols-2 gap-3"><div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Fecha</label><Input type="date" value={directOrder.scheduled_date} onChange={(e) => setDirectOrder({ ...directOrder, scheduled_date: e.target.value })} className="bg-neutral-900 border-neutral-800" /></div><div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Operario</label><select value={directOrder.assigned_to} onChange={(e) => setDirectOrder({ ...directOrder, assigned_to: e.target.value })} className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-2 text-sm"><option value="">Sin asignar</option>{team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div></div>
            <div><label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Notas</label><textarea value={directOrder.notes} onChange={(e) => setDirectOrder({ ...directOrder, notes: e.target.value })} rows={3} className="w-full bg-neutral-900 border border-neutral-800 rounded-sm p-3 text-sm" placeholder="Detalles importantes..." /></div>
            <Button onClick={saveDirectOrder} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black font-heading uppercase">Crear orden</Button>
          </div>}
        </DialogContent>
      </Dialog>

      <nav className="md:hidden fixed inset-x-0 bottom-0 z-30 no-print bg-[#111]/95 backdrop-blur-xl border-t border-white/10 px-2 pb-[env(safe-area-inset-bottom)]" aria-label="Navegación principal">
        <div className="h-[5.25rem] grid grid-cols-6 items-stretch max-w-lg mx-auto">
          {MOBILE_NAV.map((item) => {
            const active = item.matches.some((path) => path === "/panel" ? location.pathname === path : location.pathname.startsWith(path));
            return (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={`relative flex flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-semibold transition-colors ${active ? "text-[#F2B90D]" : "text-neutral-500"}`}
                aria-label={item.label}>
                {active && <span className="absolute top-0 h-1 w-8 rounded-b-full bg-[#F2B90D]" />}
                <item.icon className={`w-6 h-6 ${active ? "stroke-[2.5]" : "stroke-2"}`} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          <button onClick={() => setMenuOpen(true)}
            className={`relative flex flex-col items-center justify-center gap-1 rounded-2xl text-[11px] font-semibold transition-colors ${menuOpen || ["/panel/materiales", "/panel/whatsapp", "/panel/equipo", "/panel/empresa"].some((path) => location.pathname.startsWith(path)) ? "text-[#F2B90D]" : "text-neutral-500"}`}
            aria-label="Más secciones" data-testid="mobile-menu-open">
            <MoreHorizontal className="w-6 h-6" />
            <span>Más</span>
          </button>
        </div>
      </nav>
    </div>
    </QuoteEditorProvider>
  );
}
